"use client"

import { useState, useEffect } from 'react'

export type NavbarEffectsConfig = {
    snowEnabled: boolean
    snowDensity: number
    logoTransparentUrl: string
    logoScrolledUrl: string
    lottieEnabled: boolean
    lottieTransparentUrl: string
    lottieScrolledUrl: string
    fireworksEnabled: boolean
    bgLottieEnabled: boolean
    bgLottieTransparentUrl: string
    bgLottieScrolledUrl: string
    bgLottieOpacity: number
    startAt: string | null
    endAt: string | null
    active: boolean
}

const DEFAULT_CONFIG: NavbarEffectsConfig = {
    snowEnabled: true,
    snowDensity: 48,
    logoTransparentUrl: '/logos/Logo_white.png',
    logoScrolledUrl: '/logos/Logo_black.png',
    lottieEnabled: true,
    lottieTransparentUrl: '',
    lottieScrolledUrl: '',
    fireworksEnabled: false,
    bgLottieEnabled: false,
    bgLottieTransparentUrl: '',
    bgLottieScrolledUrl: '',
    bgLottieOpacity: 30,
    startAt: null,
    endAt: null,
    active: true,
}

// Global cache to avoid re-fetching on every mount
let cachedConfig: NavbarEffectsConfig | null = null
let cacheTimestamp = 0
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export function useNavbarEffects() {
    const [config, setConfig] = useState<NavbarEffectsConfig>(cachedConfig || DEFAULT_CONFIG)
    const [loading, setLoading] = useState(!cachedConfig)

    useEffect(() => {
        // If cache is fresh, don't refetch
        if (cachedConfig && Date.now() - cacheTimestamp < CACHE_TTL) {
            setConfig(cachedConfig)
            setLoading(false)
            return
        }

        let cancelled = false

        async function fetchConfig() {
            try {
                const res = await fetch('/api/navbar-effects', {
                    next: { revalidate: 300 } // Cache for 5 minutes
                })

                if (res.ok) {
                    const data = await res.json()
                    if (!cancelled) {
                        cachedConfig = data
                        cacheTimestamp = Date.now()
                        setConfig(data)
                    }
                }
            } catch (err) {
                console.warn('Failed to load navbar effects config:', err)
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        fetchConfig()
        return () => { cancelled = true }
    }, [])

    return { config, loading }
}

// Invalidate cache (call after admin saves changes)
export function invalidateNavbarEffectsCache() {
    cachedConfig = null
    cacheTimestamp = 0
}
